import { useState, useEffect, useRef } from 'react'
import { getUserData } from '../utils/auth';
import { apiRequest, trickleCreateObject, trickleListObjects, trickleUpdateObject } from '../utils/api';
import VoiceRecordItem from '../components/voice/VoiceRecordItem';
import Toast from '../components/Toast';
import ApiKeyModal from '../components/voice/ApiKeyModal';
import AvatarsSection from '../components/voice/AvatarsSection';
import AvatarStatusIndicator from '../components/voice/AvatarStatusIndicator';
import EmptyStateVoiceRecords from '../components/voice/EmptyStateVoiceRecords';
import { formatLanguage, getVoiceName } from '../utils/voiceUtils';
import AvatarGenerationModal from '../components/voice/AvatarGenerationModal';
import { dispatchCreditUpdateEvent } from '../utils/creditSystem';

function VoiceRecords() {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [voiceRecords, setVoiceRecords] = useState([]);
    const [filterType, setFilterType] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);
    const [currentPlayingId, setCurrentPlayingId] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
    const [avatarGenerationStatus, setAvatarGenerationStatus] = useState(null);
    const [hasApiKey, setHasApiKey] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
    const [generatedAvatars, setGeneratedAvatars] = useState([]);
    const [isLoadingAvatars, setIsLoadingAvatars] = useState(false);
    const [isLoadingHeygenKey, setIsLoadingHeygenKey] = useState(false);
    const [isAvatarGenerationModel, setIsAvatarGenerationModel] = useState(false);
    const [currentVoice, setCurrentVoice] = useState(null);

    // Debug: Log toast state whenever it changes
    useEffect(() => {
        console.log("Toast state changed:", toast);
    }, [toast]);

    useEffect(() => {
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
            window.location.href = '/auth';
            return;
        }
        
        fetchVoiceRecords();
        checkHeygenApiKey();
        fetchGeneratedAvatars();
        
        // Listen for showMessage events
        const handleShowMessage = (event) => {
            if (event.detail) {
                setToast({
                    show: true,
                    message: event.detail.text || event.detail.message,
                    type: event.detail.type || 'info'
                });
            }
        };
        
        window.addEventListener('showMessage', handleShowMessage);
        
        return () => {
            window.removeEventListener('showMessage', handleShowMessage);
        };
    }, []);

    // Add event listener to audio element
    useEffect(() => {
        const audioElement = audioRef.current;
        if (audioElement) {
            const handlePlay = () => setIsPlaying(true);
            const handlePause = () => setIsPlaying(false);
            const handleEnded = () => {
                setIsPlaying(false);
                setCurrentPlayingId(null);
            };
            
            audioElement.addEventListener('play', handlePlay);
            audioElement.addEventListener('pause', handlePause);
            audioElement.addEventListener('ended', handleEnded);
            
            return () => {
                audioElement.removeEventListener('play', handlePlay);
                audioElement.removeEventListener('pause', handlePause);
                audioElement.removeEventListener('ended', handleEnded);
            };
        }
    }, [audioRef.current]);


    const fetchVoiceRecords = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const userData = getUserData();
            if (!userData || !userData.id) {
                throw new Error('User not authenticated');
            }
            
            // Get all voice records from the dedicated collection
            const result = await apiRequest('POST', '/api/index/getVoiceRecords')
            
            if (result.success) {
                setVoiceRecords(result.data.items);
            } else {
                // If no dedicated records, try to collect from history records
                // await collectVoicesFromHistory(userData.id);
            }
        } catch (error) {
            console.error('Failed to fetch voice records:', error);
            setError('無法載入語音記錄');
        } finally {
            setIsLoading(false);
        }
    };

    const checkHeygenApiKey = async () => {
        try {
            const userData = getUserData();
            if (!userData || !userData.id) {
                setHasApiKey(false);
                return;
            }
            
            const result = await apiRequest('POST', '/api/index/getHeygenKey');
            if (result.success && result.data.value) {
                setHasApiKey(true);
                setApiKey(result.data.value);
            } else {
                setHasApiKey(false);
            }
            
        } catch (error) {
            console.error('Failed to check for API key:', error);
            setHasApiKey(false);
        }
    };

    const fetchGeneratedAvatars = async () => {
        setIsLoadingAvatars(true);
        try {
            const userData = getUserData();
            if (!userData || !userData.id) {
                return;
            }
            
            const result = await apiRequest('POST', '/api/index/getVideos')
            
            if (result.success) {
                setGeneratedAvatars(result.data.items);
            } else {
                setGeneratedAvatars([]);
            }
        } catch (error) {
            console.error('Failed to fetch avatars:', error);
        } finally {
            setIsLoadingAvatars(false);
        }
    };

    try {

        const collectVoicesFromHistory = async (userId) => {
            try {
                // Get all history records
                const historyObjectType = `history:${userId}`;
                const historyResult = await trickleListObjects(historyObjectType, 100, true);
                
                const allVoices = [];
                
                // Collect voices from history records
                for (const historyItem of historyResult.items) {
                    const record = historyItem.objectData;
                    
                    // Collect main content voices
                    if (record.generatedVoices && record.generatedVoices.length > 0) {
                        for (const voice of record.generatedVoices) {
                            allVoices.push({
                                ...voice,
                                historyRecordId: historyItem.objectId,
                                originalContent: record.content.substring(0, 200) + (record.content.length > 200 ? '...' : '')
                            });
                        }
                    }
                    
                    // Collect voices from rewrite results
                    if (record.rewriteResults && record.rewriteResults.length > 0) {
                        for (const [rewriteIndex, rewrite] of record.rewriteResults.entries()) {
                            if (rewrite.generatedVoices && rewrite.generatedVoices.length > 0) {
                                for (const voice of rewrite.generatedVoices) {
                                    allVoices.push({
                                        ...voice,
                                        historyRecordId: historyItem.objectId,
                                        rewriteIndex,
                                        originalContent: rewrite.content.substring(0, 200) + (rewrite.content.length > 200 ? '...' : '')
                                    });
                                }
                            }
                        }
                    }
                }
                
                // Save collected voices to dedicated collection
                if (allVoices.length > 0) {
                    const voiceRecordsObjectType = `voice_records:${userId}`;
                    const savePromises = allVoices.map(voice => 
                        trickleCreateObject(voiceRecordsObjectType, {
                            ...voice,
                            createdAt: voice.createdAt || new Date().toISOString()
                        })
                    );
                    
                    await Promise.all(savePromises);
                    
                    // Set the collected voices
                    setVoiceRecords(allVoices);
                } else {
                    setVoiceRecords([]);
                }
            } catch (error) {
                console.error('Failed to collect voices from history:', error);
                setVoiceRecords([]);
            }
        };

        const handlePlayVoice = async (voice) => {
            if (!audioRef.current) return;
            
            if (currentPlayingId === voice.id) {
                // If this is already playing, pause it
                if (isPlaying) {
                    audioRef.current.pause();
                } else {
                    audioRef.current.play().catch(err => {
                        console.error('Failed to play audio:', err);
                        setToast({
                            show: true,
                            message: '播放失敗，請稍後再試',
                            type: 'error'
                        });
                    });
                }
            } else {
                // Play a different voice
                setSelectedVoice(voice);
                setCurrentPlayingId(voice.id);
                audioRef.current.src = voice.url;
                audioRef.current.load();
                audioRef.current.play().catch(err => {
                    console.error('Failed to play audio:', err);
                    setToast({
                        show: true,
                        message: '播放失敗，請稍後再試',
                        type: 'error'
                    });
                });
            }
        };

        const handleCopy = async (text) => {
            try {
                await navigator.clipboard.writeText(text);
                setToast({
                    show: true,
                    message: '已複製到剪貼簿',
                    type: 'success'
                });
            } catch (err) {
                setToast({
                    show: true,
                    message: '複製失敗',
                    type: 'error'
                });
            }
        };

        const handleFilterChange = (e) => {
            setFilterType(e.target.value);
        };

        const handleSearchChange = (e) => {
            setSearchQuery(e.target.value);
        };

        const handleShowGenerateModel = (voice) => {
            if (!hasApiKey) {
                setToast({
                    show: true,
                    message: '請先設定 HeyGen API Key',
                    type: 'error'
                });
                
                setIsApiKeyModalOpen(true);
                return;
            }

            if (!voice) {
                setToast({
                    show: true,
                    message: '請先選擇一個語音記錄',
                    type: 'error'
                });
                return;
            }

            setCurrentVoice(voice);
            setIsAvatarGenerationModel(true);
        }

        const handleGenerateAvatar = async (avatarId, videoSize, voice) => {

            if (!hasApiKey) {
                console.log("No API key, opening modal");
                alert("請先設定 HeyGen API Key");
                setIsApiKeyModalOpen(true);
                return;
            }

            if (!voice) {
                setToast({
                    show: true,
                    message: '請先選擇一個語音記錄',
                    type: 'error'
                });
                return;
            }

            if (!avatarId) {
                setToast({
                    show: true,
                    message: '請輸入 Avatar ID',
                    type: 'error'
                });
                return;
            }
            
            setIsGeneratingAvatar(true);
            setAvatarGenerationStatus('preparing');
            setSelectedVoice(voice);
            
            try {
                setAvatarGenerationStatus('uploading');
                
                // Send to webhook with binary data
                const result = await apiRequest('POST', '/api/index/generateVideo', {
                    id: voice.id,
                    avatarId,
                    videoSize
                });

                if (result.data.credit) {
                    dispatchCreditUpdateEvent(result.data.credit);
                }
                if (result.success) {
                    setAvatarGenerationStatus('success');

                    setToast({
                        show: true,
                        message: '語音資產已成功上傳至 HeyGen，您可以在 HeyGen 平台上使用此資產生成數字人影片',
                        type: 'success'
                    });
                    setIsAvatarGenerationModel(false);
                    fetchGeneratedAvatars();
                } else {
                    throw new Error(result.error)
                }
                
            } catch (error) {
                setAvatarGenerationStatus('error');
                setToast({
                    show: true,
                    message: error.message || '上傳語音資產失敗',
                    type: 'error'
                });
            } finally {
                setIsGeneratingAvatar(false);
                setAvatarGenerationStatus(null);
            }
        };

        const saveApiKey = async (newApiKey) => {
            setIsLoadingHeygenKey(true);
            try {
                const userData = getUserData();
                if (!userData || !userData.id) {
                    throw new Error('User not authenticated');
                }
                
                const result = await apiRequest('POST', '/api/index/saveHeygenKey', { value: newApiKey });
                if (result.success) {
                    setApiKey(newApiKey);
                    setHasApiKey(true);
                    setIsApiKeyModalOpen(false);
                    
                    setToast({
                        show: true,
                        message: 'API Key 已成功儲存',
                        type: 'success'
                    });
                } else {
                    throw new Error(result.error)
                }
                
            } catch (error) {
                setToast({
                    show: true,
                    message: '儲存 API Key 失敗',
                    type: 'error'
                });
            } finally {
                setIsLoadingHeygenKey(false);
            }
        };

        // Function to fetch audio as binary data
        const fetchAudioAsBinary = async (url) => {
            try {
                console.log("Fetching audio from URL:", url);
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error('Failed to fetch audio file');
                }
                
                // Get the audio file as ArrayBuffer (binary data)
                const arrayBuffer = await response.arrayBuffer();
                return arrayBuffer;
            } catch (error) {
                console.error('Error fetching audio as binary:', error);
                throw error;
            }
        };

        // Filter and search voice records
        const filteredRecords = voiceRecords.filter(record => {
            // Filter by type
            if (filterType !== 'all' && record.language !== filterType) {
                return false;
            }
            
            // Search by voice name or content
            if (searchQuery) {
                const lowerQuery = searchQuery.toLowerCase();
                return (
                    record.voiceName?.toLowerCase().includes(lowerQuery) ||
                    record.content?.toLowerCase().includes(lowerQuery) ||
                    record.text?.toLowerCase().includes(lowerQuery)
                );
            }
            
            return true;
        });

        // Get unique language values for filter options
        const languageOptions = [...new Set(voiceRecords.map(record => record.language))];

        // Reset filters
        const handleResetFilters = () => {
            setSearchQuery('');
            setFilterType('all');
        };

        // Show message directly (for debugging)
        const showMessage = (message, type = 'info') => {
            console.log("Setting toast directly with showMessage function:", {
                show: true,
                message: message,
                type: type
            });
            setToast({
                show: true,
                message: message,
                type: type
            });
            
            // Also try dispatching a CustomEvent for testing
            console.log("Dispatching CustomEvent from showMessage function");
            window.dispatchEvent(new CustomEvent('showMessage', {
                detail: {
                    text: message,
                    type: type
                }
            }));
        };

        return (
            <div className="container mx-auto px-4 py-8" data-name="voice-records-page" style={{"paddingTop":"32px","paddingBottom":"32px","paddingLeft":"16px","paddingRight":"16px","marginTop":"0px","marginBottom":"0px","marginLeft":"auto","marginRight":"auto","fontSize":"16px","color":"rgb(0, 0, 0)","backgroundColor":"rgba(0, 0, 0, 0)"}}>
                <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div>
                        <h1 className="text-2xl font-bold mb-2">語音記錄</h1>
                        <p className="text-gray-600">
                            所有已生成的 AI 語音記錄與 HeyGen 數字人資產
                        </p>
                    </div>
                    <div className="mt-4 md:mt-0">
                        <button 
                            onClick={() => {
                                setIsApiKeyModalOpen(true);
                            }}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
                            data-name="api-key-button"
                        >
                            <i className="fas fa-key mr-2"></i>
                            {hasApiKey ? '更新 HeyGen API Key' : '設定 HeyGen API Key'}
                        </button>
                    </div>
                </div>
                
                {/* HeyGen Avatars Section */}
                <AvatarsSection 
                    isLoadingAvatars={isLoadingAvatars}
                    generatedAvatars={generatedAvatars}
                    hasApiKey={hasApiKey}
                    onOpenApiKeyModal={() => setIsApiKeyModalOpen(true)}
                    onCopyAssetId={handleCopy}
                />
                
                {/* Voice Records Filters */}
                <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center">
                    <div className="relative flex-grow">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            placeholder="搜尋語音記錄..."
                            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                            data-name="search-input"
                        />
                        <i className="fas fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                    
                    <select
                        value={filterType}
                        onChange={handleFilterChange}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
                        data-name="filter-select"
                    >
                        <option value="all">所有語言</option>
                        {languageOptions.map(lang => (
                            <option key={lang} value={lang}>
                                {formatLanguage(lang)}
                            </option>
                        ))}
                    </select>
                    
                    <button
                        onClick={fetchVoiceRecords}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center"
                        data-name="refresh-button"
                    >
                        <i className="fas fa-sync-alt mr-2"></i>
                        重新整理
                    </button>
                </div>
                
                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded" role="alert" data-name="error-message">
                        <div className="flex">
                            <i className="fas fa-exclamation-circle text-red-400 mr-3"></i>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                )}
                
                {/* Loading State */}
                {isLoading ? (
                    <div className="flex justify-center items-center py-12" data-name="loading">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : filteredRecords.length === 0 ? (
                    <EmptyStateVoiceRecords 
                        searchQuery={searchQuery} 
                        filterType={filterType} 
                        onResetFilters={handleResetFilters} 
                        onOpenApiKeyModal={() => setIsApiKeyModalOpen(true)}
                        hasApiKey={hasApiKey}
                    />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-name="voice-records-grid">
                        {filteredRecords.map(record => (
                            <VoiceRecordItem
                                key={record.id}
                                voice={record}
                                currentPlayingId={currentPlayingId}
                                isPlaying={isPlaying}
                                onPlayVoice={handlePlayVoice}
                                onCopy={handleCopy}
                                onGenerateAvatar={handleShowGenerateModel}
                                hasApiKey={hasApiKey}
                            />
                        ))}
                    </div>
                )}
                
                {/* Audio Player (hidden) */}
                <audio ref={audioRef} className="hidden" data-name="audio-player" />
                
                {/* Avatar Generation Status Indicator */}
                {avatarGenerationStatus && (
                    <AvatarStatusIndicator 
                        status={avatarGenerationStatus} 
                        selectedVoice={selectedVoice} 
                    />
                )}
                
                {/* API Key Modal */}
                {isApiKeyModalOpen && (
                    <ApiKeyModal 
                        isOpen={isApiKeyModalOpen}
                        onClose={() => setIsApiKeyModalOpen(false)}
                        apiKey={apiKey}
                        setApiKey={setApiKey}
                        onSave={saveApiKey}
                        isLoadingHeygenKey={isLoadingHeygenKey}
                    />
                )}
                
                {/* Avatar Generation Modal */}
                {isAvatarGenerationModel && (
                    <AvatarGenerationModal 
                        isOpen={isAvatarGenerationModel}
                        onClose={() => {
                            setIsAvatarGenerationModel(false)
                            setCurrentVoice(null);
                        }}
                        onGenerate={handleGenerateAvatar}
                        isLoading={isGeneratingAvatar}
                        voice={currentVoice}
                    />
                )}
                
                {/* Toast Notification */}
                {toast.show && (
                    <Toast 
                        message={toast.message} 
                        type={toast.type} 
                        onClose={() => {
                            console.log("Closing toast");
                            setToast({ ...toast, show: false });
                        }} 
                    />
                )}
            </div>
        );
    } catch (error) {
        console.error('VoiceRecords page error:', error);
        reportError(error);
        return null;
    }
}

export default VoiceRecords;